<template>
  <div class="rounded-xl flex flex-col flex-1 overflow-hidden">
    <div
      class="grid grid-cols-7 w-full flex-flex-col flex-1 overflow-hidden gap-4"
    >
      <div
        class="bg-white rounded-xl shadow-lg flex flex-col flex-1 overflow-hidden col-span-2 mb-4 border"
      >
        <p class="text-primary-900 text-sm p-4 border-b mx-2">
          {{ $t("chat.omni.active_title") }}
        </p>
        <simple-bar
          class="flex flex-col flex-1 overflow-y-scroll"
          v-if="omniActiveConversations?.length > 0"
        >
          <Button
            v-for="item in omniActiveConversations"
            :key="item.conversation_id"
            :class="[
              'flex items-center bg-white py-6 px-4 w-full hover:!bg-light-grey cursor-pointer',
              item.conversation_id == omniOpenedConversation?.conversation_id
                ? '!bg-light-grey'
                : 'bg-white',
            ]"
            @click="
              handleConversationOpen(
                item.conversation_id,
                converseAccess || manageAccess
              )
            "
          >
            <v-icon class="me-2 text-primary-700" size="40">{{
              getMediaIcon(item.omni_type)
            }}</v-icon>
            <div class="w-10/12">
              <div class="flex justify-between items-center w-full">
                <div class="flex items-center gap-4">
                  <p class="text-primary-900 text-sm font-semibold">
                    {{ item.customer }}
                  </p>
                  <Badge
                    v-if="manageAccess"
                    :value="item.not_read_msgs.length"
                    class="bg-primary-500 text-white text-xs rounded-sm px-2"
                  >
                    <v-icon class="text-white">mdi-account-outline</v-icon>
                    {{ item.agent }}
                  </Badge>
                </div>
                <div
                  v-if="item.last_update"
                  class="flex items-center gap-1 text-xs"
                >
                  <v-icon class="text-primary-500">mdi-clock-outline</v-icon>
                  <p class="text-primary-500">
                    {{ getConversationTime(item.last_update) }}
                  </p>
                </div>
              </div>
              <div class="flex justify-between items-center w-full">
                <p
                  :class="[
                    'text-primary-900 text-sm mt-1',
                    item.last_message ? '' : 'text-primary-400',
                  ]"
                >
                  {{
                    item.last_message
                      ? item.last_message.slice(0, 30) +
                        (item.last_message.length > 30 ? "..." : "")
                      : ""
                  }}
                </p>
                <Badge
                  v-if="item.not_read_msgs?.length > 0"
                  :value="item.not_read_msgs.length"
                  class="bg-primary-500 text-white text-xs font-semibold rounded-full mt-2"
                />
              </div>
            </div>
          </Button>
        </simple-bar>
        <div
          v-else
          class="flex flex-col flex-1 items-center justify-center overflow-y-auto"
        >
          {{ $t("chat.omni.no_active_conversations") }}
        </div>
      </div>
      <div
        class="flex flex-col flex-1 overflow-hidden col-span-3 rounded-e-xl bg-white rounded-xl shadow-lg mb-4"
        v-if="omniOpenedConversation"
      >
        <div class="flex flex-col flex-1 w-full overflow-hidden">
          <!-- Header -->
          <div
            class="flex items-center gap-3 p-4 rounded-t-xl flex-shrink-0 border-b"
          >
            <Avatar
              class="bg-primary-900 me-4"
              shape="circle"
              size="large"
              :image="require('@/assets/user.png')"
            />
            <div class="flex justify-between w-full">
              <div class="font-semiBold text-sm flex items-center gap-2">
                {{ omniOpenedConversation.customer }}
                <Badge
                  v-if="manageAccess"
                  class="bg-primary-500 text-white text-xs rounded-md p-2"
                >
                  <v-icon class="text-white">mdi-account-outline</v-icon>
                  {{ omniOpenedConversation.agent }}
                </Badge>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  class="bg-primary-900 text-white rounded-lg py-2 px-4 hover:!bg-primary-700"
                  :label="$t('chat.omni.assign_btn')"
                  @click="toggleMenu"
                />
                <Menu
                  ref="menu"
                  id="overlay_menu"
                  :model="omniUsers"
                  :popup="true"
                  class="w-fit"
                  :pt="{
                    root: {
                      dir: 'ltr',
                      class: 'bg-white border-none p-2',
                    },
                  }"
                >
                  <template #item="{ item }">
                    <div
                      v-if="item.agent !== omniOpenedConversation.agent"
                      class="flex items-center gap-2 min-w-[200px] hover:!bg-grey-100 cursor-pointer"
                      @click="
                        handleAssignAgent(
                          omniActiveConversations
                            .map((conversation) => conversation.conversation_id)
                            .indexOf(omniOpenedConversation.conversation_id),
                          omniUsers.indexOf(item)
                        )
                      "
                    >
                      <label class="text-primary-900">{{ item.agent }}</label>
                    </div>
                  </template>
                </Menu>
                <Button
                  class="bg-red-700 text-white border !border-red-500 rounded-lg py-2 px-4 hover:!bg-red-500"
                  :label="$t('chat.omni.close_btn')"
                  @click="
                    handleCloseConversation(
                      omniOpenedConversation.conversation_id
                    )
                  "
                ></Button>
              </div>
            </div>
          </div>

          <!-- Messages -->
          <div class="flex flex-col flex-1 overflow-y-auto bg-surface-50 py-2">
            <div class="relative">
              <div
                v-if="observedDay"
                class="sticky top-0 left-1/2 transform -translate-x-1/2 w-fit text-white px-3 py-1 rounded-full text-sm z-10"
              >
                {{ observedDay }}
              </div>

              <div
                ref="messagesList"
                id="messagesList"
                class="overflow-y-auto px-3"
              >
                <div v-if="!conversationLoading">
                  <div
                    v-for="(msg, i) in omniOpenedConversation.messages
                      .slice()
                      .reverse()"
                    :key="i"
                    class="mb-1"
                  >
                    <div :ref="`${msg.id}`" class="grid justify-items-stretch">
                      <div
                        v-if="
                          !isSameDayMessages(
                            msg.time,
                            i == 0
                              ? 0
                              : omniOpenedConversation.messages
                                  .slice()
                                  .reverse()[i - 1].time
                          )
                        "
                        class="text-primary-900 text-sm my-2 w-fit justify-self-center bg-nature-grey py-1 px-2 rounded-lg"
                      >
                        {{ getConversationTime(msg.time, 3) }}
                      </div>

                      <div
                        v-if="msg.type === 1"
                        class="text-sm text-primary-900 bg-white px-4 py-2 rounded-lg shadow-md my-2 w-fit justify-self-end"
                      >
                        {{ msg.message }}
                        <div class="text-left text-xs text-gray-500 mt-1">
                          {{ getMessageTimeFormat(msg.time) }}
                        </div>
                      </div>
                      <div
                        v-else
                        class="text-sm text-dark bg-nature-grey px-4 py-2 rounded-lg shadow-md my-2 w-fit justify-self-start"
                      >
                        {{ msg.message }}
                        <div class="text-end text-xs text-gray-500 mt-1">
                          {{ getMessageTimeFormat(msg.time) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="flex justify-center p-4">
                  <ProgressSpinner
                    style="width: 50px; height: 50px"
                    strokeWidth="8"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Input -->
          <div
            v-if="!manageAccess"
            class="flex items-center gap-2 p-4 rounded-b-xl border-t"
          >
            <Textarea
              v-model="messageText"
              autoResize
              rows="1"
              class="w-full"
              :placeholder="$t('chat.message_input_placeholder')"
              @keydown.enter="handleEnter"
            />
            <Button
              class="!text-primary-500 hover:!bg-primary-100 rounded-full p-2"
              @click="sendMessage"
            >
              <v-icon
                :class="[$i18n.locale === 'ar' ? 'transform scale-x-[-1]' : '']"
                >mdi-send</v-icon
              ></Button
            >
          </div>
        </div>
      </div>
      <div
        v-if="omniOpenedConversation"
        class="flex flex-col flex-1 overflow-hidden rounded-e-xl gap-4 col-span-2"
      >
        <caller-info
          class="bg-white"
          :senderId="omniOpenedConversation.customer"
        />
        <last-tickets
          class="bg-white flex-1 shadow-lg mb-4"
          :senderId="omniOpenedConversation.customer"
        />
      </div>
      <div
        v-else
        class="flex flex-col flex-1 items-center justify-center overflow-y-auto col-span-5 text-gray-500"
      >
        {{ $t("chat.omni.select_conversation_to_view") }}
      </div>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  Button,
  Textarea,
  ProgressSpinner,
  Badge,
  Avatar,
  Menu,
} from "primevue";
import CallerInfo from "@/pages/call/components/CallerInfo.vue";
import LastTickets from "@/pages/call/components/LastTickets.vue";

export default {
  components: {
    Button,
    Textarea,
    ProgressSpinner,
    Badge,
    Avatar,
    CallerInfo,
    LastTickets,
    Menu,
  },
  data() {
    return {
      // isInit: false,
      agentsMenuOpened: [],
      agentsAssign: [],
      messageLoading: "",
      observer: null,
      pullDownThreshold: 64,
      messagesLimit: 30,
      messagesLabels: [],
      observeList: [],
      observedDay: null,
      converseHeight: 0,
      // closeOverLay: false,
      // closingTime: null,
      closedCustomer: "",
      // focusSelect: false,
      // searchedUser: "",
      // selectedUser: "",
      // selectedConversation: null,
      scrollInvoked: 0,
      conversationLoading: false,
      messageText: "",
      jumpToID: null,
      scrollBehavior: null,
      // omniChannelAgents: [
      //   { agent: "105", total: 3 },
      //   { agent: "112", total: 5 },
      //   { agent: "109", total: 2 },
      //   { agent: "101", total: 1 },
      //   { agent: "104", total: 6 },
      // ],
      // isConversationSelected: [],
      // activeConversations: [
      // {
      //   conversation_id: "104_011237439821",
      //   count: 9,
      //   last_message: "fifth",
      //   last_update: "1732481634",
      //   omni_type: 3,
      //   messages: [],
      //   not_read_msgs: [{ message: "fifth" }, { message: "fourth" }],
      //   customer: "011237439821",
      //   agent: "105",
      // },
      // {
      //   conversation_id: "105_083278322332",
      //   count: 19,
      //   last_message: "مرحبا بك",
      //   last_update: "1731340978",
      //   messages: [],
      //   omni_type: 5,
      //   not_read_msgs: [{ message: "مرحبا بك" }],
      //   customer: "083278322332",
      //   agent: "104",
      // },
      // ],
    };
  },
  computed: {
    ...mapGetters([
      "uname",
      "notReadOmniMessages",
      // "incomingSocketOmniMessage",
      "chatSocket",
      "socketKey",
      // "omniDifferentTABSent",
      // "omniDifferentTABRead",
      // "omniConversationClosed",
      // "assignedOmniConversation",
      // "currentOmniID",
      "ManageOmniChannelPermission",
      "omniChannelPermission",
      "permissions",
      "omniActiveConversations",
      "omniUsers",
      "omniOpenedConversation",
      "omniCloseOverlay",
      "handleOpenedOmniConversation",
      "omniClosedCustomer",
    ]),
    currentLocal() {
      return this.$vuetify.locale.current;
    },
    converseAccess() {
      return this.userHasPermisson(
        this.permissions,
        this.omniChannelPermission
      );
    },
    manageAccess() {
      return this.userHasPermisson(
        this.permissions,
        this.ManageOmniChannelPermission
      );
    },
  },
  methods: {
    ...mapMutations([
      "setNotReadOmniMessages",
      "setCurrentOmniID",
      "setOmniActiveConversations",
      "setOmniUsers",
      "setOmniOpenedConversation",
      "setOmniCloseOverlay",
      "setOmniClosedCustomer",
    ]),
    ...mapActions([
      // "GetUserActiveOmniConversations",
      // "GetCurrentActiveOmniConversations",
      // "GetOmniChannelUsers",
      "GetOmniConversationMessages",
      "CloseOmniConversation",
      "ReassignOmniConversation",
      "SetMessageDialog",
      "ShowProgressBar",
    ]),
    async loadMoreMessages({ done }) {
      // Perform API call
      console.log("loading");
      try {
        const resp = await this.GetOmniConversationMessages({
          conversation_id: this.omniOpenedConversation.conversation_id,
          offset: this.omniOpenedConversation.messages.length,
          limit: this.messagesLimit,
        });
        if (!resp.success) {
          done("ok");
          this.SetMessageDialog({ show: true, message: resp.message });
          return;
        }
        this.jumpToID =
          this.omniOpenedConversation.messages[
            this.omniOpenedConversation.messages.length - 1
          ].id;
        const converseIDX = this.getConversationIndex(
          this.omniOpenedConversation.conversation_id
        );
        this.handleSelectedConversation(
          converseIDX,
          true,
          resp.messages,
          false
        );
        done("ok");
      } catch (err) {
        let message = err.message;
        if (err.response) {
          message = err.response.data.message;
        }
        this.conversationLoading = false;
        this.SetMessageDialog({ show: true, message: message });
        done("ok");
      }
    },
    // async initChat() {
    //   if (this.isInit) {
    //     return;
    //   }
    //   this.isInit = true;
    //   try {
    //     let conversResp;
    //     if (this.manageAccess) {
    //       conversResp = await this.GetCurrentActiveOmniConversations();
    //     } else if (this.converseAccess) {
    //       conversResp = await this.GetUserActiveOmniConversations();
    //     }
    //     if (!conversResp.success) {
    //       // this.ShowProgressBar({ show: false, message: "" });
    //       this.SetMessageDialog({ show: true, message: conversResp.message });
    //       return;
    //     }
    //     const result = conversResp.conversations;
    //     const initConversations = [];
    //     for (const res of result) {
    //       // const user = this.usersList.find((usr) => usr.uname == res.uname);
    //       const converse = { ...res, isSet: res.messages.length > 0 };
    //       initConversations.push(converse);
    //     }
    //     this.activeConversations = initConversations;
    //     if (this.manageAccess) {
    //       const agentsResp = await this.GetOmniChannelUsers();
    //       if (!agentsResp.success) {
    //         // this.ShowProgressBar({ show: false, message: "" });
    //         this.SetMessageDialog({ show: true, message: agentsResp.message });
    //         return;
    //       }
    //       this.omniChannelAgents = agentsResp.agents;
    //     }
    //     // this.ShowProgressBar({ show: false, message: "" });
    //   } catch (err) {
    //     let message = err.message;
    //     if (err.response) {
    //       message = err.response.data.message;
    //     }
    //     // this.ShowProgressBar({ show: false, message: "" });
    //     this.SetMessageDialog({ show: true, message: message });
    //   }
    // },
    onScroll() {
      this.scrollInvoked++;
    },
    setupObserver() {
      if (!this.$refs.messagesList) {
        return;
      }
      this.observeList = [];
      this.observedDay = null;
      // console.log("My setUpObserver", this.$refs.messagesList);
      if (this.observer) {
        this.observer.disconnect();
      }
      const options = {
        root: this.$refs.messagesList,
        rootMargin: "0px",
        threshold: 0.7,
      };
      this.observer = new IntersectionObserver(this.handleShowingDay, options);
      this.messagesLabels.forEach((messageDay) => {
        // console.log("My day Ele", messageDay, this.$refs[messageDay][0]);
        this.observer.observe(this.$refs[messageDay][0]);
      });
    },
    handleShowingDay(entries) {
      entries.forEach((entry) => {
        // console.log(
        //   "My intersecting, parent, entry , value",
        //   entry.isIntersecting,
        //   this.$refs.messagesList.scrollTop,
        //   entry.target.offsetTop,
        //   entry.target.innerText
        // );
        if (!this.omniOpenedConversation) {
          return;
        }
        const value = entry.target.innerText;
        if (!this.observeList.includes(value)) {
          this.observeList.push(value);
          if (this.observeList.length != this.messagesLabels.length) {
            return;
          }
        }
        const parentOffset = this.$refs.messagesList.scrollTop;
        const isIntersect = entry.isIntersecting;
        const entryOffset = entry.target.offsetTop;
        const showPrevious = !isIntersect && parentOffset < entryOffset;
        const showValueCond =
          (isIntersect && parentOffset < entryOffset) ||
          (isIntersect && entryOffset < 0) ||
          (!isIntersect && parentOffset > entryOffset);
        if (value.toLowerCase() == this.$t("chat.time.today")) {
          if (showPrevious) {
            const idx = this.observeList.findIndex((val) => val == value);
            this.observedDay =
              idx != -1 && idx > 0 ? this.observeList[idx - 1] : value;
          } else {
            this.observedDay = null;
          }
          return;
        }
        if (showValueCond) {
          this.observedDay = value;
        } else if (showPrevious) {
          const idx = this.observeList.findIndex((val) => val == value);
          this.observedDay =
            idx != -1 && idx > 0 ? this.observeList[idx - 1] : value;
        }
      });
    },
    getMediaIcon(type) {
      let icon;
      switch (type) {
        case 1:
          icon = "mdi-message";
          break;
        case 2:
          icon = "mdi-email";
          break;
        case 3:
          icon = "mdi-whatsapp";
          break;
        case 4:
          icon = "mdi-send-circle";
          break;
        case 5:
          icon = "mdi-facebook";
          break;
        case 6:
          icon = "mdi-instagram";
          break;
        case 7:
          icon = "mdi-twitter";
          break;
        default:
          icon = "mdi-message";
      }
      return icon;
    },
    handleConversationOpen(converseID, selected) {
      if (!selected) {
        return;
      }
      this.setCustomerConversation(converseID);
      // this.selectedConversation = this.activeConversations[index];
    },
    getConversationTime(lastTime, type) {
      const messageDay = moment.unix(+lastTime).format("YYYY-MM-DD");
      const today = moment().format("YYYY-MM-DD");
      const sameDayCond = moment(messageDay).isSame(moment(today), "day");
      if (sameDayCond && type != 3) {
        return moment.unix(+lastTime).format("HH:mm");
      } else if (sameDayCond && type == 3) {
        return this.$t("chat.time.today");
      }
      if (moment(today).diff(moment(messageDay), "days") == 1) {
        return this.$t("chat.time.yesterday");
      }
      const weekEarlier = moment().add(-7, "days").format("YYYY-MM-DD");
      if (moment(weekEarlier).isBefore(moment(messageDay))) {
        return moment.unix(+lastTime).locale(this.currentLocal).format("dddd");
        // return moment.unix(+lastTime).format("dddd");
      }
      return messageDay;
    },
    getMessageDate(msgTime) {
      return moment.unix(+msgTime).format("YYYY-MM-DD");
    },
    getMessageTimeFormat(msgTime) {
      return moment.unix(+msgTime).format("HH:mm");
    },
    isSameDayMessages(msgTime, prevMsgTime) {
      const messageDay = moment.unix(+msgTime).format("YYYY-MM-DD");
      const prevMessageDay = moment.unix(+prevMsgTime).format("YYYY-MM-DD");
      const sameDayCond = moment(messageDay).isSame(
        moment(prevMessageDay),
        "day"
      );
      if (sameDayCond) {
        return true;
      }
      if (!sameDayCond || msgTime == prevMessageDay) {
        if (!this.messagesLabels.includes(messageDay)) {
          this.messagesLabels.push(messageDay);
        }
      }
      return false;
    },
    setMessagesToRead(notReadMSG, converseID) {
      if (notReadMSG.length == 0) {
        return 0;
      }
      const newNotReadMessages = this.notReadOmniMessages.filter(
        (notRead) => !notReadMSG.includes(notRead.message_id)
      );
      this.setNotReadOmniMessages(newNotReadMessages);
      this.jumpToID = notReadMSG[0];
      // console.log("My setMessages To Read~~~", converseID);
      this.chatSocket.send(
        JSON.stringify({
          type: 34,
          not_read: notReadMSG,
          conversation_id: converseID,
        })
      );
      return notReadMSG.length;
    },
    scrollToLastMessage() {
      this.$nextTick(() => {
        let jumpID;
        if (!this.jumpToID) {
          if (this.omniOpenedConversation.messages.length == 0) {
            return;
          }
          jumpID = this.omniOpenedConversation.messages[0].id;
        } else {
          jumpID = this.jumpToID;
        }
        let behavior = this.scrollBehavior;
        if (!this.scrollBehavior) {
          behavior = "instant";
        }
        this.$refs[jumpID][0]?.scrollIntoView({
          behavior,
        });
        this.jumpToID = null;
        this.scrollBehavior = null;
      });
    },
    handleEnter(e) {
      if (e.shiftKey) return; // Allow newline
      e.preventDefault();
      this.sendMessage(e);
    },
    sendMessage(e) {
      e.preventDefault();
      if (this.messageText.trim() == "") {
        return;
      }
      let message = {
        message: this.messageText.trim(),
        time: moment().unix().toString(),
        type: 1,
        id: uuidv4(),
      };
      let exist = this.omniOpenedConversation.messages.length > 0;
      this.chatSocket.send(
        JSON.stringify({
          type: 31,
          message_id: message.id,
          from: this.uname,
          to: this.omniOpenedConversation.customer,
          text: message.message,
          conversation_id: this.omniOpenedConversation.conversation_id,
          time: message.time,
          is_read: true,
          agent: this.uname,
          customer: this.omniOpenedConversation.customer,
          omni_type: this.omniOpenedConversation.omni_type,
        })
      );
      let isSamePrevMsgDay = true;
      if (exist) {
        isSamePrevMsgDay = this.isSameDayMessages(
          message.time,
          this.omniOpenedConversation.messages[0].time
        );
      }
      const idx = this.getConversationIndex(
        this.omniOpenedConversation.conversation_id
      );
      this.scrollBehavior = "smooth";
      this.handleSelectedConversation(idx, !isSamePrevMsgDay, [message], true);
      this.messageText = "";
    },
    resendMessage(msgId) {
      this.messageLoading = msgId;
      const msgIdx = this.omniOpenedConversation.messages.findIndex(
        (msg) => msg.id == msgId
      );
      // console.log("My resend MEssage:::", msgId);
      this.chatSocket.send(
        JSON.stringify({
          type: 31,
          message_id: msgId,
          from: this.uname,
          to: this.omniOpenedConversation.customer,
          text: this.omniOpenedConversation.messages[msgIdx].message,
          conversation_id: this.omniOpenedConversation.conversation_id,
          time: this.omniOpenedConversation.messages[msgIdx].time,
          is_read: true,
          agent: this.uname,
          customer: this.omniOpenedConversation.customer,
          omni_type: this.omniOpenedConversation.omni_type,
        })
      );
      setTimeout(() => {
        const activeConversations = JSON.parse(
          JSON.stringify(this.omniActiveConversations)
        );
        const converseIdx = this.getConversationIndex(
          this.omniOpenedConversation.conversation_id
        );
        activeConversations[converseIdx].messages[msgIdx].failed = false;
        this.setOmniOpenedConversation(activeConversations[converseIdx]);
        this.setOmniActiveConversations(activeConversations);
        this.messageLoading = "";
        // const message = this.omniOpenedConversation.messages[msgIdx];
        // console.log(
        //   "Message loading",
        //   this.messageLoading,
        //   this.omniOpenedConversation.messages[msgIdx]
        // );
      }, 800);
    },
    async setCustomerConversation(converseID) {
      this.conversationLoading = true;
      // this.messagesLabels = [];
      const converseIDX = this.getConversationIndex(converseID);

      // console.log("My set customer::", converseIDX);
      let conversation;
      // if (converseIDX == -1) {
      //   // const converseUser = this.usersList.find(
      //   //   (element) => element.uname == customer
      //   // );
      //   conversation = {
      //     // ...converseUser,
      //     customer: customer,
      //     agent: agent,
      //     omniType: omniType,
      //     conversation_id: converseID,
      //     isSet: true,
      //     messages: [],
      //     last_message: "",
      //     last_update: "",
      //     count: 0,
      //     not_read_msgs: [],
      //   };
      // } else
      if (this.omniActiveConversations[converseIDX].isSet) {
        conversation = JSON.parse(
          JSON.stringify(this.omniActiveConversations[converseIDX])
        );
      } else if (!this.omniActiveConversations[converseIDX].isSet) {
        conversation = JSON.parse(
          JSON.stringify(this.omniActiveConversations[converseIDX])
        );
        try {
          const resp = await this.GetOmniConversationMessages({
            conversation_id: conversation.conversation_id,
            offset: 0,
            limit: this.messagesLimit,
          });

          if (!resp.success) {
            this.conversationLoading = false;
            this.SetMessageDialog({ show: true, message: resp.message });
            return;
          }
          conversation.messages = resp.messages;
          // if (conversation.messages.length > 0) {
          //   conversation.isSet = true;
          // }
          // conversation.not_read_msgs = resp.not_read_msgs;
        } catch (err) {
          let message = err.message;
          if (err.response) {
            message = err.response.data.message;
          }
          this.conversationLoading = false;
          this.SetMessageDialog({ show: true, message: message });
        }
      }
      this.handleSelectedConversation(
        converseIDX,
        true,
        [],
        false,
        conversation
      );
    },
    handleSelectedConversation(
      idx,
      isDiffrentDay,
      messages,
      fromStart,
      conversation
    ) {
      if (isDiffrentDay) {
        this.messagesLabels = [];
      }
      if (!conversation) {
        conversation = JSON.parse(JSON.stringify(this.omniOpenedConversation));
      }
      if (messages.length > 0) {
        if (fromStart) {
          conversation.messages.unshift(...messages);
          conversation.last_update = messages[0].time;
          conversation.last_message = messages[0].message;
        } else {
          conversation.messages.push(...messages);
        }
        conversation.count += messages.length;
      }
      const updateSetcond =
        conversation.messages.length > 0 && !conversation.isSet;
      if (updateSetcond) {
        conversation.isSet = true;
      }
      let oldCount = 0;
      if (this.converseAccess && conversation.agent == this.uname) {
        oldCount = this.setMessagesToRead(
          conversation.not_read_msgs,
          conversation.conversation_id
        );
        conversation.not_read_msgs = [];
      }
      // this.selectedConversation = JSON.parse(JSON.stringify(conversation));
      this.setOmniOpenedConversation(JSON.parse(JSON.stringify(conversation)));
      this.conversationLoading = false;
      const activeConversations = JSON.parse(
        JSON.stringify(this.omniActiveConversations)
      );
      if (idx == -1) {
        activeConversations.unshift(JSON.parse(JSON.stringify(conversation)));
      } else if (messages.length > 0 || oldCount > 0 || updateSetcond) {
        activeConversations[idx] = JSON.parse(JSON.stringify(conversation));
        if (fromStart) {
          activeConversations.unshift(activeConversations.splice(idx, 1)[0]);
        }
      }
      this.setOmniActiveConversations(
        JSON.parse(JSON.stringify(activeConversations))
      );
      if (isDiffrentDay) {
        setTimeout(() => {
          this.setupObserver();
        }, 500);
      }
      this.scrollToLastMessage();
    },
    getConversationIndex(converseID) {
      return this.omniActiveConversations.findIndex(
        (converse) => converse.conversation_id == converseID
      );
    },
    async handleCloseConversation(converseID) {
      this.ShowProgressBar({
        show: true,
        message: this.$t("chat.omni.closing_message", {
          customer: this.omniOpenedConversation?.customer,
        }),
      });
      const idx = this.getConversationIndex(converseID);
      this.setOmniCloseOverlay(true);
      this.setOmniClosedCustomer(this.omniActiveConversations[idx].customer);
      try {
        const resp = await this.CloseOmniConversation({
          conversation_id: converseID,
          not_read: this.omniActiveConversations[idx].not_read_msgs,
          agent: this.omniActiveConversations[idx].agent
            ? this.omniActiveConversations[idx].agent
            : "",
          conn_key: this.socketKey,
        });

        if (!resp.success) {
          this.setOmniCloseOverlay(false);
          this.setOmniClosedCustomer("");
          this.SetMessageDialog({ show: true, message: resp.message });
          return;
        }
        if (this.omniOpenedConversation?.conversation_id == converseID) {
          // this.selectedConversation = null;
          this.setOmniOpenedConversation(null);
        }
        if (this.omniActiveConversations[idx].not_read_msgs.length > 0) {
          const newNotReadMessages = this.notReadOmniMessages.filter(
            (notRead) =>
              !this.omniActiveConversations[idx].not_read_msgs.includes(
                notRead.message_id
              )
          );
          this.setNotReadOmniMessages(newNotReadMessages);
        }
        if (this.manageAccess) {
          const oldAgentIDX = this.omniUsers.findIndex(
            (omniAgent) =>
              omniAgent.agent == this.omniActiveConversations[idx].agent
          );
          // this.omniChannelAgents[oldAgentIDX].total += -1;
          const agents = JSON.parse(JSON.stringify(this.omniUsers));
          agents[oldAgentIDX].total += -1;
          this.setOmniUsers(agents);
        }
        const activeConversations = JSON.parse(
          JSON.stringify(this.omniActiveConversations)
        );
        activeConversations.splice(idx, 1);
        this.setOmniActiveConversations(activeConversations);
        // this.closeOverLay = false;
        this.setOmniCloseOverlay(false);
        this.setOmniClosedCustomer("");
      } catch (err) {
        let message = err.message;
        if (err.response) {
          message = err.response.data.message;
        }
        this.setOmniCloseOverlay(false);
        this.setOmniClosedCustomer("");
        this.SetMessageDialog({ show: true, message: message });
      } finally {
        this.ShowProgressBar({ show: false, message: "" });
      }
    },
    //  handleCloseConversation(converseID) {
    //   // this.closeOverLay = true;
    //   this.setOmniCloseOverlay(true);
    //   const idx = this.getConversationIndex(converseID);
    //   this.setOmniClosedCustomer(this.omniActiveConversations[idx].customer);
    //   this.chatSocket.send(
    //     JSON.stringify({
    //       type: 37,
    //       conversation_id: converseID,
    //       not_read: this.omniActiveConversations[idx].not_read_msgs,
    //       agent: this.omniActiveConversations[idx].agent
    //         ? this.omniActiveConversations[idx].agent
    //         : "",
    //     })
    //   );
    //   setTimeout(() => {
    //     if (this.omniOpenedConversation?.conversation_id == converseID) {
    //       // this.selectedConversation = null;
    //       this.setOmniOpenedConversation(null);
    //     }
    //     if (this.omniActiveConversations[idx].not_read_msgs.length > 0) {
    //       const newNotReadMessages = this.notReadOmniMessages.filter(
    //         (notRead) =>
    //           !this.omniActiveConversations[idx].not_read_msgs.includes(
    //             notRead.message_id
    //           )
    //       );
    //       this.setNotReadOmniMessages(newNotReadMessages);
    //     }
    //     if (this.manageAccess) {
    //       const oldAgentIDX = this.omniUsers.findIndex(
    //         (omniAgent) =>
    //           omniAgent.agent == this.omniActiveConversations[idx].agent
    //       );
    //       // this.omniChannelAgents[oldAgentIDX].total += -1;
    //       const agents = JSON.parse(JSON.stringify(this.omniUsers));
    //       agents[oldAgentIDX].total += -1;
    //       this.setOmniUsers(agents);
    //     }
    //     const activeConversations = JSON.parse(
    //       JSON.stringify(this.omniActiveConversations)
    //     );
    //     activeConversations.splice(idx, 1);
    //     this.setOmniActiveConversations(activeConversations);
    //     // this.closeOverLay = false;
    //     this.setOmniCloseOverlay(false);
    //     this.setOmniClosedCustomer("");
    //   }, 1300);
    // },
    async handleAssignAgent(converseIDx, agentIDX) {
      try {
        this.ShowProgressBar({
          show: true,
          message: this.$t("chat.omni.assigning_agent", {
            customer: this.omniActiveConversations[converseIDx].customer,
          }),
        });
        this.agentsAssign[converseIDx] = true;
        const resp = await this.ReassignOmniConversation({
          conversation_id:
            this.omniActiveConversations[converseIDx].conversation_id,
          not_read: this.omniActiveConversations[converseIDx].not_read_msgs,
          current_agent: this.omniActiveConversations[converseIDx].agent,
          assigned_to: this.omniUsers[agentIDX].agent,
          conn_key: this.socketKey,
        });

        if (!resp.success) {
          this.agentsAssign[converseIDx] = false;
          this.SetMessageDialog({ show: true, message: resp.message });
          return;
        }
        if (this.manageAccess) {
          const oldAgentIDX = this.omniUsers.findIndex(
            (omniAgent) =>
              omniAgent.agent == this.omniActiveConversations[converseIDx].agent
          );
          const agents = JSON.parse(JSON.stringify(this.omniUsers));
          agents[agentIDX].total += 1;
          agents[oldAgentIDX].total += -1;
          this.setOmniUsers(agents);
        }
        const activeConversations = JSON.parse(
          JSON.stringify(this.omniActiveConversations)
        );
        activeConversations[converseIDx].agent = this.omniUsers[agentIDX].agent;
        this.setOmniActiveConversations(activeConversations);
        if (
          this.omniOpenedConversation &&
          this.omniOpenedConversation?.conversation_id ==
            activeConversations[converseIDx].conversation_id
        ) {
          this.setOmniOpenedConversation(activeConversations[converseIDx]);
        }
        this.agentsAssign[converseIDx] = false;
      } catch (err) {
        let message = err.message;
        if (err.response) {
          message = err.response.data.message;
        }
        this.agentsAssign[converseIDx] = false;
        this.SetMessageDialog({ show: true, message: message });
      } finally {
        this.ShowProgressBar({ show: false, message: "" });
      }
      this.agentsAssign[converseIDx] = false;
    },
    toggleMenu(e) {
      this.$refs.menu.toggle(e);
    },
    // handleAssignAgent(converseIDx, agentIDX) {
    //   this.agentsAssign[converseIDx] = true;
    //   let lastMSG = this.omniActiveConversations[converseIDx].messages[0];
    //   if (
    //     !lastMSG ||
    //     this.omniActiveConversations[converseIDx].messages.length == 0
    //   ) {
    //     lastMSG = {
    //       message: this.omniActiveConversations[converseIDx].last_message,
    //       time: this.omniActiveConversations[converseIDx].last_update,
    //       id: "",
    //     };
    //   }
    //   console.log("My handleassign before socket send");
    //   this.chatSocket.send(
    //     JSON.stringify({
    //       type: 38,
    //       to: this.omniUsers[agentIDX].agent,
    //       omni_type: this.omniActiveConversations[converseIDx].omni_type,
    //       conversation_id:
    //         this.omniActiveConversations[converseIDx].conversation_id,
    //       agent: this.omniActiveConversations[converseIDx].agent,
    //       text: lastMSG.message,
    //       time: lastMSG.time,
    //       message_id: lastMSG.id,
    //       customer: this.omniActiveConversations[converseIDx].customer,
    //       not_read: this.omniActiveConversations[converseIDx].not_read_msgs,
    //     })
    //   );
    //   console.log("My handleassign after socket send");

    //   if (this.manageAccess) {
    //     const oldAgentIDX = this.omniUsers.findIndex(
    //       (omniAgent) =>
    //         omniAgent.agent == this.omniActiveConversations[converseIDx].agent
    //     );
    //     const agents = JSON.parse(JSON.stringify(this.omniUsers));
    //     agents[agentIDX].total += 1;
    //     agents[oldAgentIDX].total += -1;
    //     this.setOmniUsers(agents);
    //   }
    //   const activeConversations = JSON.parse(
    //     JSON.stringify(this.omniActiveConversations)
    //   );
    //   activeConversations[converseIDx].agent = this.omniUsers[agentIDX].agent;
    //   this.setOmniActiveConversations(activeConversations);
    //   if (
    //     this.omniOpenedConversation &&
    //     this.omniOpenedConversation?.conversation_id ==
    //       activeConversations[converseIDx].conversation_id
    //   ) {
    //     this.setOmniOpenedConversation(activeConversations[converseIDx]);
    //   }
    //   console.log("My handleassign end");

    //   this.agentsAssign[converseIDx] = false;
    // },
  },
  watch: {
    // closeOverLay(value) {
    //   console.log("My overaly value changed", value);
    //   // if (!value) {
    //   //   clearTimeout(this.closingTime);
    //   // }
    // },
    omniOpenedConversation: {
      handler: async function (value) {
        // console.log(
        //   "My omniOpned watch!!!!",
        //   value,
        //   this.omniActiveConversations
        // );
        if (!value) {
          this.setCurrentOmniID(null);
          if (this.observer) {
            this.observer.disconnect();
          }
        } else {
          this.setCurrentOmniID(value.conversation_id);
        }
      },
      deep: true,
      immediate: true,
    },
    handleOpenedOmniConversation(process) {
      if (!process) {
        return;
      }
      this.handleSelectedConversation(
        process.idx,
        process.isDiffrentDay,
        process.messages,
        process.fromStart
      );
    },
  },
  async created() {
    // await this.initChat();
  },
  mounted() {
    if (this.omniOpenedConversation) {
      this.handleSelectedConversation(
        this.getConversationIndex(this.omniOpenedConversation.conversation_id),
        true,
        [],
        false,
        JSON.parse(JSON.stringify(this.omniOpenedConversation))
      );
    }
  },
  unmounted() {
    this.setCurrentOmniID(null);
    this.setOmniOpenedConversation(null);
  },
};
</script>

<style scoped></style>
